import React, { useMemo, useState } from 'react';
import { locName, siteLocations, isArchived, devicesAtSite, subLocationsFor, subLocationNameFree } from '../../utils/locations';

// Fold an existing site into another as a sub-location.
//
// This is the manual counterpart to the portal's auto-map: the same two-level shape,
// arrived at by hand because the new portal has no API to derive it from.
//
// What it does, in one action:
//   1. creates a stored sub-location on the parent, named after this site
//   2. moves every device here to the parent, tagged with that sub-location
//   3. copies the contact across, so nobody loses the number to call
//   4. archives this site, keeping its batches and movement history readable
//
// Archiving rather than deleting is what makes it reversible: the old site can be
// restored, and every device carries the move in its own locationLog.

function MoveSiteIntoLocation({ loc, st, onCancel, onConfirm }) {
  const here = locName(loc);
  const devices = useMemo(() => devicesAtSite(st.devices || [], loc), [st.devices, loc]);

  // Candidate parents: every other active site. An archived site is not a deploy
  // destination, so folding live devices into one would strand them.
  const parents = useMemo(
    () => siteLocations(st.locations || [])
      .filter(l => locName(l) !== here && !isArchived(l))
      .sort((a, b) => locName(a).localeCompare(locName(b))),
    [st.locations, here]
  );

  const [parent, setParent] = useState('');
  const [name, setName] = useState(here);
  const [keepContact, setKeepContact] = useState(true);

  const trimmed = name.trim();
  const parentLoc = parents.find(l => locName(l) === parent) || null;
  const existing = useMemo(
    () => (parentLoc ? subLocationsFor(parentLoc, st) : []),
    [parentLoc, st]
  );

  const nameTaken = !!parentLoc && !!trimmed && !subLocationNameFree(trimmed, parent, st);
  const canSubmit = !!parentLoc && !!trimmed && !nameTaken;

  const label = { fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, display: 'block' };
  const control = {
    width: '100%', height: 34, padding: '0 10px', borderRadius: 6,
    border: '1px solid var(--border-default)', background: 'var(--surface-default)',
    color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14, outline: 'none',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Move ${here} into another location`}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="card" style={{ width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border-default)' }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>Move {here} into a location</span>
          <button type="button" onClick={onCancel} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)' }}>
            {here} becomes a sub-location of the location you pick. Its devices move there with it,
            and {here} is archived — its history stays readable, but it stops being a deploy destination.
          </div>

          <div>
            <label htmlFor="msl-parent" style={label}>Parent location <span style={{ color: 'var(--brand-light)' }}>*</span></label>
            <select id="msl-parent" value={parent} onChange={e => setParent(e.target.value)} style={control}>
              <option value="">Choose a location…</option>
              {parents.map(l => <option key={locName(l)} value={locName(l)}>{locName(l)}</option>)}
            </select>
            {!parents.length && (
              <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 6 }}>
                There is no other active location to move this into. Create one first.
              </div>
            )}
          </div>

          <div>
            <label htmlFor="msl-name" style={label}>Sub-location name <span style={{ color: 'var(--brand-light)' }}>*</span></label>
            <input
              id="msl-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              aria-invalid={nameTaken || undefined}
              style={{ ...control, borderColor: nameTaken ? 'var(--danger-border)' : 'var(--border-default)' }} />
            <div style={{ fontSize: 12, color: nameTaken ? 'var(--danger-text)' : 'var(--text-muted)', marginTop: 6 }}>
              {nameTaken
                ? `${parent} already has a sub-location called “${trimmed}”. Pick another name.`
                : 'Defaults to the site name. This is what devices here will be tagged with.'}
            </div>
          </div>

          {!!existing.length && (
            <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--surface-inset)', border: '1px solid var(--border-default)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                {parent} already holds {existing.length} sub-location{existing.length === 1 ? '' : 's'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {existing.slice(0, 6).map(s => s.name).join(', ')}
                {existing.length > 6 ? ` and ${existing.length - 6} more` : ''}
              </div>
            </div>
          )}

          {(loc.contact || loc.phone || loc.email) && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={keepContact} onChange={e => setKeepContact(e.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                Keep this site&rsquo;s contact on the sub-location
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {[loc.contact, loc.phone].filter(Boolean).join(' · ') || loc.email}
                </span>
              </span>
            </label>
          )}

          <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--surface-inset)', border: '1px solid var(--border-default)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>What will happen</div>
            {[
              devices.length
                ? `${devices.length} device${devices.length === 1 ? '' : 's'} move to ${parent || 'the parent location'}, tagged “${trimmed || here}”`
                : 'No devices are on this site, so none move',
              `“${trimmed || here}” is added as a sub-location of ${parent || 'the parent location'}`,
              `${here} is archived — batches, contacts and history are kept`,
              'Each device records the move in its own history',
            ].map((line, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)', padding: '3px 0' }}>
                <span aria-hidden="true" style={{ color: 'var(--text-subtle)' }}>•</span>
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '14px 18px', borderTop: '1px solid var(--border-default)' }}>
          <button type="button" onClick={onCancel} className="btn btn-ghost">Cancel</button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm({ parent, name: trimmed, keepContact })}
            className="btn btn-primary"
            style={{ opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            Move site
          </button>
        </div>
      </div>
    </div>
  );
}

export default MoveSiteIntoLocation;
